import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { ArrowLeft, Lock, CheckCircle, Loader2, CreditCard, Smartphone } from 'lucide-react';
import api from '@/lib/api';

declare global {
  interface Window { Razorpay?: any }
}

// The publishable key is served by the backend (super-admin managed) so the
// platform can rotate keys / switch provider without a frontend redeploy.
// Falls back to the Vite build-time env if the API hasn't been configured yet.
const FALLBACK_PK = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder';

type Provider = 'stripe' | 'razorpay';

interface PayConfig {
  activeProvider: string | null;
  stripeEnabled: boolean;
  stripePublishableKey: string | null;
  razorpayEnabled: boolean;
  razorpayKeyId: string | null;
  currency: string;
  usdToInr: number;
}

const CARD_STYLE = {
  style: {
    base: {
      fontSize: '14px',
      color: '#1E293B',
      fontFamily: 'Inter, sans-serif',
      '::placeholder': { color: '#94A3B8' },
    },
    invalid: { color: '#EF4444' },
  },
};

// Load the Razorpay checkout script once, on demand.
let razorpayScriptPromise: Promise<boolean> | null = null;
function loadRazorpayScript(): Promise<boolean> {
  if (window.Razorpay) return Promise.resolve(true);
  if (razorpayScriptPromise) return razorpayScriptPromise;
  razorpayScriptPromise = new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => { razorpayScriptPromise = null; resolve(false); };
    document.body.appendChild(s);
  });
  return razorpayScriptPromise;
}

// ── Shared views ──────────────────────────────────────────────────────────────

function SuccessView({ planName }: { planName: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
        <CheckCircle className="h-8 w-8 text-emerald-500" />
      </div>
      <h3 className="text-xl font-bold text-slate-800 mb-2">Payment successful!</h3>
      <p className="text-slate-500 text-sm">Activating your {planName} plan…</p>
      <Loader2 className="h-5 w-5 animate-spin text-[#6366F1] mt-4" />
    </div>
  );
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
      {children}
    </div>
  );
}

// ── Stripe form (needs Stripe context) ────────────────────────────────────────

function StripeCheckoutForm({ planId, planName, price, billingCycle }: { planId: string; planName: string; price: number; billingCycle: 'monthly' | 'yearly' }) {
  const navigate = useNavigate();
  const { refreshPermissions } = useAuth();
  const stripe = useStripe();
  const elements = useElements();

  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [isLoadingIntent, setIsLoadingIntent] = useState(true);
  const [isPaying, setIsPaying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    api.post('/payments/create-intent', { planId, billingCycle })
      .then(r => {
        setClientSecret(r.data.clientSecret);
        setPaymentIntentId(r.data.paymentIntentId);
      })
      .catch(err => setError(err?.response?.data?.message || 'Could not initialize payment.'))
      .finally(() => setIsLoadingIntent(false));
  }, [planId]);

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;

    setIsPaying(true);
    setError('');

    const cardNumber = elements.getElement(CardNumberElement);
    if (!cardNumber) { setIsPaying(false); return; }

    const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: { card: cardNumber },
    });

    if (stripeError) {
      setError(stripeError.message || 'Payment failed. Please try again.');
      setIsPaying(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      try {
        await api.post('/payments/confirm', { paymentIntentId, planId });
        // Refresh plan modules so sidebar shows the correct menu
        // for the newly activated subscription immediately.
        await refreshPermissions();
        setSuccess(true);
        setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
      } catch {
        setError('Payment succeeded but activation failed. Please contact support.');
      }
    }

    setIsPaying(false);
  };

  if (success) return <SuccessView planName={planName} />;

  if (isLoadingIntent) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[#6366F1]" />
      </div>
    );
  }

  return (
    <form onSubmit={handlePay} className="space-y-5">
      {/* Card Number */}
      <div>
        <label className="block text-xs font-normal text-slate-600 mb-1.5">Card Number</label>
        <div className="h-11 px-3 flex items-center rounded-lg border border-gray-200 bg-white focus-within:ring-2 focus-within:ring-[#6366F1] focus-within:border-transparent">
          <CardNumberElement options={CARD_STYLE} className="flex-1" />
          <CreditCard className="h-4 w-4 text-gray-400 ml-2 flex-shrink-0" />
        </div>
      </div>

      {/* Expiry + CVC */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-normal text-slate-600 mb-1.5">Expiry Date</label>
          <div className="h-11 px-3 flex items-center rounded-lg border border-gray-200 bg-white focus-within:ring-2 focus-within:ring-[#6366F1] focus-within:border-transparent">
            <CardExpiryElement options={CARD_STYLE} className="flex-1" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-normal text-slate-600 mb-1.5">CVC</label>
          <div className="h-11 px-3 flex items-center rounded-lg border border-gray-200 bg-white focus-within:ring-2 focus-within:ring-[#6366F1] focus-within:border-transparent">
            <CardCvcElement options={CARD_STYLE} className="flex-1" />
          </div>
        </div>
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}

      <button
        type="submit"
        disabled={isPaying || !stripe}
        className="w-full h-12 bg-[#6366F1] hover:bg-[#4F46E5] text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2 shadow-md shadow-indigo-200"
      >
        {isPaying
          ? <><Loader2 className="h-4 w-4 animate-spin" /> Processing…</>
          : <><Lock className="h-4 w-4" /> Pay ${price} & Activate</>
        }
      </button>

      <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1.5">
        <Lock className="h-3 w-3" />
        Secured by Stripe. We never store your card details.
      </p>
    </form>
  );
}

// ── Razorpay checkout (hosted modal) ──────────────────────────────────────────

function RazorpayCheckout({ planId, planName, inrAmount, billingCycle }: { planId: string; planName: string; inrAmount: number; billingCycle: 'monthly' | 'yearly' }) {
  const navigate = useNavigate();
  const { refreshPermissions } = useAuth();
  const [isPaying, setIsPaying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handlePay = async () => {
    setIsPaying(true);
    setError('');

    const ok = await loadRazorpayScript();
    if (!ok || !window.Razorpay) {
      setError('Could not load the payment gateway. Please try again.');
      setIsPaying(false);
      return;
    }

    let order: any;
    try {
      const r = await api.post('/payments/razorpay/create-order', { planId, billingCycle });
      order = r.data;
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not initialize payment.');
      setIsPaying(false);
      return;
    }

    const rzp = new window.Razorpay({
      key: order.keyId,
      amount: order.amount,
      currency: order.currency,
      name: 'BrixLead',
      description: `${planName} Plan — Monthly subscription`,
      order_id: order.orderId,
      theme: { color: '#6366F1' },
      handler: async (resp: any) => {
        try {
          await api.post('/payments/razorpay/confirm', {
            razorpayOrderId: resp.razorpay_order_id,
            razorpayPaymentId: resp.razorpay_payment_id,
            razorpaySignature: resp.razorpay_signature,
            planId,
            billingCycle,
          });
          await refreshPermissions();
          setSuccess(true);
          setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
        } catch {
          setError('Payment succeeded but activation failed. Please contact support.');
          setIsPaying(false);
        }
      },
      modal: { ondismiss: () => setIsPaying(false) },
    });
    rzp.on('payment.failed', (resp: any) => {
      setError(resp?.error?.description || 'Payment failed. Please try again.');
      setIsPaying(false);
    });
    rzp.open();
  };

  if (success) return <SuccessView planName={planName} />;

  return (
    <div className="space-y-5">
      {error && <ErrorBox>{error}</ErrorBox>}

      <div className="rounded-lg bg-slate-50 border border-slate-100 px-4 py-4 flex items-start gap-3">
        <Smartphone className="h-5 w-5 text-[#6366F1] flex-shrink-0 mt-0.5" />
        <p className="text-sm text-slate-600">
          You’ll pay securely through Razorpay — UPI, netbanking, wallets, or card. Charged in INR.
        </p>
      </div>

      <button
        onClick={handlePay}
        disabled={isPaying}
        className="w-full h-12 bg-[#6366F1] hover:bg-[#4F46E5] text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2 shadow-md shadow-indigo-200"
      >
        {isPaying
          ? <><Loader2 className="h-4 w-4 animate-spin" /> Processing…</>
          : <><Lock className="h-4 w-4" /> Pay ₹{inrAmount.toLocaleString('en-IN')} & Activate</>
        }
      </button>

      <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1.5">
        <Lock className="h-3 w-3" />
        Secured by Razorpay. We never store your card details.
      </p>
    </div>
  );
}

// ── Page wrapper ──────────────────────────────────────────────────────────────

export function PaymentPage() {
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('planId') || '';
  const planName = searchParams.get('planName') || '';
  const price = Number(searchParams.get('price') || 0);
  const billingCycle = (searchParams.get('cycle') === 'yearly' ? 'yearly' : 'monthly') as 'monthly' | 'yearly';

  const [cfg, setCfg] = useState<PayConfig | null>(null);
  const [cfgFailed, setCfgFailed] = useState(false);
  const [offered, setOffered] = useState<Provider[]>([]);
  const [selected, setSelected] = useState<Provider | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  // Resolve which gateway(s) the checkout should show. The super-admin's
  // activeProvider is authoritative: if it names a provider (and that provider
  // is actually configured), only that one is offered; otherwise every
  // configured provider is offered and the user picks.
  useEffect(() => {
    api.get('/payments/config')
      .then(({ data }) => {
        const c = data as PayConfig;
        setCfg(c);
        const available: Provider[] = [];
        if (c.stripeEnabled) available.push('stripe');
        if (c.razorpayEnabled) available.push('razorpay');
        const active = c.activeProvider as Provider | null;
        const list = active && available.includes(active) ? [active] : available;
        setOffered(list);
        setSelected(list[0] ?? null);
        if (list.includes('stripe')) {
          setStripePromise(loadStripe(c.stripePublishableKey || FALLBACK_PK));
        }
      })
      .catch(() => {
        // Config endpoint unavailable (e.g. old backend) → fall back to Stripe
        // so checkout still works.
        setCfgFailed(true);
        setOffered(['stripe']);
        setSelected('stripe');
        setStripePromise(loadStripe(FALLBACK_PK));
      });
  }, []);

  const perks = [
    'Instant account activation',
    'Cancel anytime',
    'No setup fees',
    'Dedicated onboarding support',
  ];

  const usdToInr = cfg?.usdToInr || 83;
  const inrAmount = Math.round(price * usdToInr);
  const showInr = selected === 'razorpay';
  const totalDisplay = showInr ? `₹${inrAmount.toLocaleString('en-IN')}` : `$${price}`;

  const loadingConfig = !cfg && !cfgFailed;
  const noProvider = !loadingConfig && offered.length === 0;

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="w-full max-w-4xl">
        {/* Back link */}
        <Link
          to="/choose-plan"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#6366F1] mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back to plans
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {/* Left: Order summary */}
          <div className="md:col-span-2">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="text-base font-bold text-slate-800 mb-5">Order Summary</h2>

              {/* Plan row */}
              <div className="flex items-center justify-between py-3 border-b border-gray-50">
                <div>
                  <p className="text-sm font-semibold text-slate-800">{planName} Plan</p>
                  <p className="text-xs text-slate-400">{billingCycle === 'yearly' ? 'Yearly subscription' : 'Monthly subscription'}</p>
                </div>
                <span className="text-sm font-bold text-slate-800">${price}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-gray-50">
                <p className="text-sm text-slate-500">Tax</p>
                <span className="text-sm text-slate-500">$0.00</span>
              </div>
              <div className="flex items-center justify-between pt-3">
                <p className="text-sm font-bold text-slate-800">Total today</p>
                <span className="text-lg font-extrabold text-[#6366F1]">{totalDisplay}</span>
              </div>
              {showInr && (
                <p className="text-[11px] text-slate-400 mt-1 text-right">Charged in INR (≈ ${price} USD)</p>
              )}

              {/* Perks */}
              <div className="mt-6 space-y-2.5">
                {perks.map(perk => (
                  <div key={perk} className="flex items-center gap-2 text-xs text-slate-500">
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                    {perk}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Payment form */}
          <div className="md:col-span-3">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="text-base font-bold text-slate-800 mb-5">Payment Details</h2>

              {loadingConfig ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-6 w-6 animate-spin text-[#6366F1]" />
                </div>
              ) : noProvider ? (
                <ErrorBox>Online payments aren’t configured yet. Please contact support to activate this plan.</ErrorBox>
              ) : (
                <>
                  {/* Provider selector — only when more than one is offered */}
                  {offered.length > 1 && selected && (
                    <div className="flex gap-2 mb-5">
                      {offered.map(p => {
                        const active = selected === p;
                        return (
                          <button
                            key={p}
                            onClick={() => setSelected(p)}
                            className={`flex-1 h-10 rounded-lg text-sm font-semibold border transition-all flex items-center justify-center gap-1.5 ${
                              active
                                ? 'border-[#6366F1] bg-indigo-50 text-[#6366F1]'
                                : 'border-gray-200 text-slate-500 hover:border-gray-300'
                            }`}
                          >
                            {p === 'stripe' ? <><CreditCard className="h-4 w-4" /> Card</> : <><Smartphone className="h-4 w-4" /> UPI / Razorpay</>}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {selected === 'stripe' && stripePromise && (
                    <Elements stripe={stripePromise}>
                      <StripeCheckoutForm planId={planId} planName={planName} price={price} billingCycle={billingCycle} />
                    </Elements>
                  )}

                  {selected === 'razorpay' && (
                    <RazorpayCheckout planId={planId} planName={planName} inrAmount={inrAmount} billingCycle={billingCycle} />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
